import $ from 'jquery'
import tippy from 'tippy.js'
import { Extension } from '@tiptap/core'
import { PluginKey } from '@tiptap/pm/state'
import Suggestion from '@tiptap/suggestion'
import { personalizationSnippet } from '~/admin/personalizations/snippets'
import { resolveTippyPortal } from '../utilities'

const pluginKey = new PluginKey('interpolationSuggestion')

function cloneTemplate(selector) {
  return document
    .querySelector(selector)
    .content.firstElementChild.cloneNode(true)
}

function buildMenuContent(items) {
  const menu = cloneTemplate('.js-personalization-menu-template')
  const ul = menu.querySelector('ul')
  const itemTemplate = cloneTemplate('.js-personalization-menu-item-template')

  items.forEach(([label, key]) => {
    const li = itemTemplate.cloneNode(true)
    li.dataset.interpolationKey = key
    li.querySelector(
      '.js-personalization-menu-item-content'
    ).innerHTML = `${label} <span class="text-fg-muted">(${key})</span>`
    ul.appendChild(li)
  })

  return menu
}

function dispatchEventToMenu(menuTarget, event) {
  menuTarget.dispatchEvent(new CustomEvent(event))
}

export const InterpolationSuggestion = Extension.create({
  name: 'interpolationSuggestion',

  addOptions() {
    return { personalizations: [], interpolationStyle: 'liquid' }
  },

  addProseMirrorPlugins() {
    // 'single_brace' inserts plain `{key}` tokens (Python str.format style, used by
    // content-pipeline prompt arguments) instead of Liquid `{{key}}` snippets.
    const singleBrace = this.options.interpolationStyle === 'single_brace'
    const allItems = this.options.personalizations.filter(
      ([key]) => key !== 'countdown_timer'
    )

    return [
      Suggestion({
        editor: this.editor,
        pluginKey,
        char: singleBrace ? '{' : '{{',
        items: ({ query }) => {
          const q = query.toLowerCase()
          return allItems.filter(
            ([key, label]) =>
              key.toLowerCase().includes(q) ||
              (label && label.toLowerCase().includes(q))
          )
        },
        command: ({ editor, range, props: key }) => {
          if (singleBrace) {
            const { tr } = editor.state
            tr.insertText(`{${key}}`, range.from, range.to)
            editor.view.dispatch(tr)
            editor.view.focus()
            return
          }
          const snippet = personalizationSnippet(key)
          if (snippet.type === 'action') return
          if (snippet.type === 'html') {
            editor.chain().deleteRange(range).insertContent(snippet.content).run()
          } else {
            const { tr } = editor.state
            tr.insertText(snippet.content, range.from, range.to)
            editor.view.dispatch(tr)
          }
          editor.view.focus()
        },
        render: () => {
          let popup
          let container

          const updateMenu = (obj) => {
            $(container).off('click', 'li a')
            popup[0].setContent(buildMenuContent(obj.items))
            $(container).on('click', 'li a', function (e) {
              const key = e.currentTarget.closest('li').dataset.interpolationKey
              if (key) obj.command(key)
            })
          }

          return {
            onBeforeStart(obj) {
              const template = document.querySelector(
                '.js-interpolation-suggestions-container-template'
              )
              container = template.content.firstElementChild.cloneNode(true)
              resolveTippyPortal().append(container)
              popup = tippy('body', {
                getReferenceClientRect: obj.clientRect,
                appendTo: () => container,
                content: buildMenuContent([]),
                showOnCreate: true,
                interactive: true,
                trigger: 'manual',
                placement: 'bottom-start',
                offset: [10, 0],
                popperOptions: {
                  strategy: 'fixed',
                },
              })
            },
            onStart: updateMenu,
            onUpdate: updateMenu,
            onKeyDown(props) {
              const key = props.event.key
              const menuTarget = popup[0].popper.querySelector(
                '.js-personalization-menu'
              )
              // Don't intercept ArrowRight: this menu has no cols/sections for it
              // to navigate, so capturing it here only blocked the caret from
              // moving past the trigger token, trapping the user until they
              // clicked away.
              if (key === 'Escape') {
                props.event.cancelBubble = true
                popup[0].hide()
              } else if (key === 'ArrowDown') {
                dispatchEventToMenu(menuTarget, 'menu:arrowDown')
              } else if (key === 'ArrowUp') {
                dispatchEventToMenu(menuTarget, 'menu:arrowUp')
              } else if (key === 'Enter' || key === 'Tab') {
                props.event.preventDefault()
                dispatchEventToMenu(menuTarget, 'menu:enterKey')
              } else {
                return false
              }

              return true
            },
            onExit() {
              popup[0].destroy()
              container.remove()
            },
          }
        },
      }),
    ]
  },
})
